import { useEffect, useState } from "react";
import { api, PHASES, INIT_TYPES } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { toast } from "sonner";
import { Plus, X, Presentation } from "@phosphor-icons/react";
import { WorkshopToolbar } from "../components/WorkshopToolbar";

const STATUS = { planned: "○", in_progress: "◐", done: "●" };

export default function Roadmap() {
  const [inits, setInits] = useState([]);
  const [depts, setDepts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [adding, setAdding] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", type: "quick_win", sponsor: "" });
  const [workshop, setWorkshop] = useState(false);

  const load = () => Promise.all([api.get("/initiatives"), api.get("/departments")]).then(([i, d]) => { setInits(i.data); setDepts(d.data); });
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    await api.post("/initiatives", { ...form, department_key: adding.dept, phase: adding.phase });
    toast.success("Initiative added to roadmap");
    setAdding(null); setForm({ title: "", description: "", type: "quick_win", sponsor: "" });
    load();
  };

  const setStatus = async (id, status) => {
    await api.put(`/initiatives/${id}`, { status });
    toast.success("Status updated");
    setSelected(null); load();
  };

  return (
    <div className="canvas-grain min-h-screen pb-16" data-testid="roadmap-page">
      <PageHeader overline="Transformation & AI Roadmap" title="Six-Phase Roadmap Matrix"
        subtitle="Departments as colour-coded rows, phases as columns — traceable to operating-model recommendations. Click a chip for detail, or a cell to add an initiative."
        actions={<button onClick={() => setWorkshop(true)} data-testid="workshop-mode-btn"
          className="flex items-center gap-1.5 text-xs bg-[#0A0A0A] text-[#D4AF37] rounded-sm px-4 py-2 font-semibold hover:bg-black/85 transition-colors"><Presentation size={14} /> Workshop mode</button>} />
      {workshop && <WorkshopToolbar title="Six-Phase Roadmap Matrix" context="roadmap" onExit={() => setWorkshop(false)} />}
      <div className="px-8 mb-4 flex gap-4 text-[11px] text-black/60 flex-wrap">
        {Object.values(INIT_TYPES).map((t) => <span key={t.label} className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: t.color }} />{t.label}</span>)}
        <span className="ml-3">○ planned · ◐ in progress · ● done</span>
      </div>
      <div className={workshop ? "fixed inset-0 top-[52px] z-40 bg-[#FDFBF7] overflow-auto p-4" : "mx-8 bg-white border border-black/10 rounded-sm overflow-x-auto"}>
        <div className="grid" style={{ gridTemplateColumns: "190px repeat(6, minmax(150px, 1fr))", minWidth: 1100 }} data-testid="roadmap-matrix">
          <div className="bg-[#0A0A0A] text-[#D4AF37] px-3 py-3 text-[11px] uppercase tracking-[0.12em] sticky left-0 z-10">Department</div>
          {PHASES.map((p, i) => (
            <div key={p} className="bg-[#0A0A0A] text-[#D4AF37] px-3 py-3 text-[11px] uppercase tracking-[0.12em] border-l border-white/10">
              {i + 1}. {p}
            </div>
          ))}
          {depts.map((d) => (
            <FragmentRow key={d.key} dept={d} inits={inits.filter((i) => i.department_key === d.key)}
              onChip={setSelected} onAdd={(phase) => setAdding({ dept: d.key, phase, deptName: d.name })} />
          ))}
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-sm p-6 w-[440px] fade-up" onClick={(e) => e.stopPropagation()} data-testid="initiative-detail-modal">
            <div className="flex justify-between items-start">
              <span className="text-[10px] uppercase tracking-wider text-white px-2 py-1 rounded-sm" style={{ background: INIT_TYPES[selected.type]?.color }}>{INIT_TYPES[selected.type]?.label}</span>
              <button onClick={() => setSelected(null)}><X size={16} /></button>
            </div>
            <h3 className="font-display font-bold text-xl tracking-tight mt-2">{selected.title}</h3>
            <p className="text-[13px] text-black/60 mt-1">{selected.description}</p>
            <div className="grid grid-cols-2 gap-3 mt-4 text-[12px]">
              <div><p className="text-[10px] uppercase tracking-[0.15em] text-black/50">Phase</p><p>{selected.phase}. {selected.phase_name}</p></div>
              <div><p className="text-[10px] uppercase tracking-[0.15em] text-black/50">Horizon</p><p>{selected.horizon}</p></div>
              <div><p className="text-[10px] uppercase tracking-[0.15em] text-black/50">Sponsor</p><p>{selected.sponsor}</p></div>
              <div><p className="text-[10px] uppercase tracking-[0.15em] text-black/50">Status</p><p className="capitalize">{selected.status.replace("_", " ")}</p></div>
            </div>
            <div className="flex gap-2 mt-5">
              {["planned", "in_progress", "done"].map((s) => (
                <button key={s} onClick={() => setStatus(selected.id, s)} data-testid={`set-status-${s}`}
                  className={`flex-1 text-xs rounded-sm py-2 border transition-colors capitalize ${selected.status === s ? "bg-[#0A0A0A] text-[#D4AF37] border-black" : "border-black/20 hover:border-[#D4AF37]"}`}>
                  {STATUS[s]} {s.replace("_", " ")}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {adding && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" onClick={() => setAdding(null)}>
          <form onSubmit={create} className="bg-white rounded-sm p-6 w-[440px] space-y-3 fade-up" onClick={(e) => e.stopPropagation()} data-testid="add-initiative-modal">
            <h3 className="font-display font-bold text-xl tracking-tight">New initiative</h3>
            <p className="text-[12px] text-black/55">{adding.deptName} — Phase {adding.phase}: {PHASES[adding.phase - 1]}</p>
            <input required placeholder="Initiative title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="initiative-title-input"
              className="w-full border border-black/15 rounded-sm px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-[#D4AF37]" />
            <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2}
              className="w-full border border-black/15 rounded-sm px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-[#D4AF37]" />
            <div className="flex gap-2">
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="flex-1 border border-black/15 rounded-sm px-2 py-2 text-sm bg-white" data-testid="initiative-type-select">
                {Object.entries(INIT_TYPES).map(([k, t]) => <option key={k} value={k}>{t.label}</option>)}
              </select>
              <input placeholder="Sponsor" value={form.sponsor} onChange={(e) => setForm({ ...form, sponsor: e.target.value })}
                className="flex-1 border border-black/15 rounded-sm px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-[#D4AF37]" />
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setAdding(null)} className="text-xs border border-black/20 rounded-sm px-3 py-2">Cancel</button>
              <button type="submit" data-testid="initiative-submit-btn" className="text-xs bg-[#0A0A0A] text-[#D4AF37] rounded-sm px-4 py-2 font-semibold">Add initiative</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function FragmentRow({ dept, inits, onChip, onAdd }) {
  return (
    <>
      <div className="px-3 py-3 text-white text-[12px] font-display font-bold sticky left-0 z-10 border-t border-white/20 flex items-center" style={{ background: dept.color }} data-testid={`roadmap-row-${dept.key}`}>
        {dept.name}
      </div>
      {[1, 2, 3, 4, 5, 6].map((phase) => {
        const cell = inits.filter((i) => i.phase === phase);
        return (
          <div key={phase} className="border-t border-l border-black/8 p-1.5 min-h-[64px] group relative" style={{ background: `${dept.color}0D` }}>
            {cell.map((i) => (
              <button key={i.id} onClick={() => onChip(i)} data-testid="roadmap-chip"
                className="chip block w-full text-left text-[10px] leading-snug text-white rounded-sm px-2 py-1.5 mb-1"
                style={{ background: dept.color, opacity: i.status === "done" ? 0.55 : 1 }}>
                <span className="float-right ml-1 font-mono">{i.status === "done" ? "●" : i.status === "in_progress" ? "◐" : "○"}</span>
                <span className="inline-block w-1.5 h-1.5 rounded-full mr-1" style={{ background: INIT_TYPES[i.type]?.color }} />
                {i.title}
              </button>
            ))}
            <button onClick={() => onAdd(phase)} data-testid={`add-init-${dept.key}-${phase}`}
              className="opacity-0 group-hover:opacity-100 transition-opacity text-[10px] text-black/40 hover:text-black flex items-center gap-0.5 px-1">
              <Plus size={10} /> add
            </button>
          </div>
        );
      })}
    </>
  );
}
